<template>
  <div>
    <app-cells>
      <app-button @click.native="onRefreshInvoices" ref="refresh">Refresh invoices</app-button>
    </app-cells>
    <v-client-table
      :data="table.items"
      :columns="table.columns"
      :options="table.options"
      class="table-default"
    >
      <template #h__count><div class="table-default__left">Invoice #</div></template>
      <template #h__payer><div class="table-default__left">Payer</div></template>
      <template #count="{ row }">
        <div class="table-default__controls table-default__left">
          <button @click="onCopyFamilyLink(row.hash)" type="button">
            <img src="@/assets/img/a-table-icon.svg" alt="">
          </button>
          <a
            :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
            target="_blank"
            class="table-default__link-border"
          >
            {{ row.number }}
          </a>
        </div>
      </template>
      <template #payer="{ row }"><div class="table-default__left">{{ row.payer }}</div></template>
      <template #deadline="{ row }">{{ dateFormat(row.deadline) }}</template>
      <template #amount="{ row }">
        {{ row.total_amount_usd ? `${row.total_amount_usd.toLocaleString()} USD` : '-'}}
      </template>
      <template #payable="{ row }">
        {{ row.amount_payable_usd ? `${row.amount_payable_usd.toLocaleString()} USD` : '-'}}
      </template>
      <template #commission="{ row }">
        {{ row.amount_payable_with_commission ? `${row.amount_payable_with_commission.toLocaleString()} USD` : '-'}}
      </template>
    </v-client-table>
    <template v-if="table_completed.items.length">
      <app-cells><h2 class="title">Completed</h2></app-cells>
      <v-client-table
        :data="table_completed.items"
        :columns="table_completed.columns"
        :options="table_completed.options"
        class="table-default"
      >
        <template #h__count><div class="table-default__left">Invoice #</div></template>
        <template #h__payer><div class="table-default__left">Payer</div></template>
        <template #h__status><div class="table-default__left">Status</div></template>
        <template #count="{ row }">
          <div class="table-default__left">
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__link-border"
            >
              {{ row.number }}
            </a>
          </div>
        </template>
        <template #payer="{ row }"><div class="table-default__left">{{ row.payer }}</div></template>
        <template #status="{ row }">
          <div class="table-default__left table-default__controls">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color }">
            {{ row.status.name }}
          </span>
          </div>
        </template>
        <template #amount="{ row }">
          {{ row.total_amount_usd ? `${row.total_amount_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #payable="{ row }">
          {{ row.amount_payable_usd ? `${row.amount_payable_usd.toLocaleString()} USD` : '-'}}
        </template>
        <template #commission="{ row }">
          {{ row.amount_payable_with_commission ? `${row.amount_payable_with_commission.toLocaleString()} USD` : '-'}}
        </template>
        <template #completed="{ row }">{{ dateTimeFormat(row.auth_form_data.completion_date) }}</template>
        <template #charged="{ row }">
          <span v-if="!row.auth_data_charge_info?.charged" class="status-tag" :style="{ 'backgroundColor': '#8a8a8a' }">No</span>
          <div v-else class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.auth_data_charge_info.charged_by.crop.crop_url)" alt="Avatar"
                v-tooltip="{ content: row.auth_data_charge_info.charged_by.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <span>{{ dateTimeFormat(row.auth_data_charge_info.charged_at) }}</span>
          </div>
        </template>
        <template #controls="{ row }">
          <div v-if="row.auth_data_charge_info?.charged === false" class="table-default__controls">
            <button
              @click="onShowDeleteModal(row.id)"
              v-tooltip="{ content: 'Delete auth form', placement: 'top-start', offset: 10 }"
              type="button"
            >
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
    </template>
    <modal
      name="delete"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete auth form
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this auth form?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteAuthForm" ref="deleteForm">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getFinanceInvoiceList, getRefreshInvoices, deleteFinanceInvoiceAuthData } from '@/http'
import { format, parse, parseISO } from 'date-fns'

export default {
  name: 'LeadsDetailInvoices',
  data() {
    return {
      form: {
        auth_data: {},
      },
      table: {
        items: [],
        columns: [
          'count',
          'payer',
          'deadline',
          'amount',
          'payable',
          'commission',
        ],
        options: {
          headings: {
            'count': 'Invoice #',
            'payer': 'Payer',
            'deadline': 'Deadline',
            'amount': 'Amount',
            'payable': 'Payable',
            'commission': 'With commission',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_completed: {
        items: [],
        columns: [
          'count',
          'payer',
          'status',
          'amount',
          'payable',
          'commission',
          'completed',
          'charged',
          'controls',
        ],
        options: {
          headings: {
            'count': 'Invoice #',
            'payer': 'Payer',
            'status': 'Status',
            'amount': 'Amount',
            'payable': 'Payable',
            'commission': 'With commission',
            'completed': 'Completed',
            'charged': 'Charged',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      invoice_to_update: null,
      family_url: process.env.VUE_APP_FAMILY,
      auth_form_to_delete: null,
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      getFinanceInvoiceList({ lead: +this.$route.params.id, charge_type: 'auth_form', authorization_form_completed: false, status: 'sent' })
        .then(response => {
          this.table.items = response.data
          this.$refs.refresh.preload = false
        })
      getFinanceInvoiceList({ lead: +this.$route.params.id, charge_type: 'auth_form', authorization_form_completed: true })
        .then(response => {
          this.table_completed.items = response.data
        })
    },
    onRefreshInvoices() {
      this.$refs.refresh.preload = true
      getRefreshInvoices(+this.$route.params.id)
        .then(() => {
          this.fetchItems()
        })
        .catch(() => { this.$refs.refresh.preload = false })
    },
    onCopyFamilyLink(invoice_hash) {
      navigator.clipboard.writeText(`${this.family_url}authorization-form/${invoice_hash}`)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Link copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    onShowDeleteModal(id) {
      this.auth_form_to_delete = id
      this.$modal.show('delete')
    },
    onDeleteAuthForm() {
      this.$refs.deleteForm.preload = true
      deleteFinanceInvoiceAuthData(this.auth_form_to_delete)
        .then(() => {
          this.fetchItems()
          this.$modal.hide('delete')
        })
        .catch(() => { this.$refs.deleteForm.preload = false })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>
